import { CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import clsx from 'clsx';
import parse, { domToReact, Element } from 'html-react-parser';
import { createElement, ElementType, useCallback, useRef, useState } from 'react';

import { Link } from '~components/generic/elements/Link/Link';
import { DEFAULT_TOOLBAR_CONTROLS } from '../constants/toolbar';
import Theme from '../Theme';
import { CustomParagraphNode } from './nodes/CustomParagraphNode';
import LexicalEditorContent from './LexicalEditorContent';
import styles from '../EditableField/EditableField.css';

type ClassNameShape = string | { [key: string]: boolean };

type TextAreaProps = {
  editing?: boolean;
  onChange?: (field: string, value: string) => void;
  value?: string;
  className?: ClassNameShape;
  excludeControls?: string[];
  controls?: string[];
  fieldName?: string;
};

const editorConfig = {
  namespace: 'Lexical TextArea Editor',
  onError(error: Error) {
    throw error;
  },
  nodes: [
    CustomParagraphNode,
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
  ],
  theme: Theme,
};

const TextAreaField = ({
  editing,
  onChange,
  value,
  className,
  controls,
  excludeControls,
  fieldName = '',
}: TextAreaProps) => {
  const targetRef = useRef(null);
  const [hasFocus, setHasFocus] = useState(false);

  const classes = clsx(
    'o-field-text relative',
    styles.base,
    editing && styles.editing,
    hasFocus && styles.focus,
    className,
  );

  const onChangeEditor = useCallback(
    (html: string) => {
      onChange?.(fieldName, html);
    },
    [onChange, fieldName],
  );

  const DynamicTag = 'div' as ElementType;

  const allControls = controls || DEFAULT_TOOLBAR_CONTROLS;
  const excluded = excludeControls || [];
  const includedControls = allControls.filter(control => excluded.indexOf(control) === -1);

  if (editing) {
    return (
      <div onFocus={() => setHasFocus(true)} onBlur={() => setHasFocus(false)}>
        <LexicalComposer initialConfig={editorConfig}>
          <LexicalEditorContent
            customClassName="text-area-field"
            includedControls={includedControls}
            withoutDynamicTag={true}
            showAsHtmlCode={false}
            targetRef={targetRef}
            editing={editing}
            data={value}
            onChangeEditor={onChangeEditor}
          />
        </LexicalComposer>
      </div>
    );
  }

  return createElement(
    DynamicTag,
    { ref: targetRef, className: classes },
    parse(value || '', {
      replace: domNode => {
        if (domNode instanceof Element) {
          const { type, name, attribs, children } = domNode;
          if (type !== 'tag' || name !== 'a') {
            return;
          }
          const { href } = attribs;
          if (href) {
            const [path, id] = href.indexOf('/#/') === -1 && href.indexOf('~') >= 1 ? href.split('#') : [href, ''];
            return createElement(Link, { id, url: !id && path }, domToReact(children as any));
          }
        }
        return;
      },
    }),
  );
};

export default TextAreaField;
